import { useState, useEffect, useMemo } from 'react';
import {
  Avatar,
  Typography,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import { Delete, Add, TimeToLeave } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useRouteLoaderData } from 'react-router-dom';
import useTitle from '@/hooks/useTitle';
import useApiQuery from '@/hooks/useApiQuery';
import useLiveData from '@/hooks/useLiveData';
import useApiDelete from '@/hooks/useApiDelete';
import useAppStore from '@/store/app';
import ListView from '@/components/datagrid/ListView';
import api from '@/services/ApiService';
import ToolForm from '@/components/forms/tool/ToolForm';
import ToolHistory from '@/components/forms/tool/ToolHistory';
import { QRCodeSVG } from 'qrcode.react';
import { MoveToInbox } from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@mui/lab';

export default function Tools() {
  const { users, me } = useRouteLoaderData('auth');
  const [selectedRows, setSelectedRows] = useState([]);
  const { selectedItem, setSelectedItem } = useAppStore();
  const [search, setSearch] = useState(null);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newToolTitle, setNewToolTitle] = useState('');
  const [selectedTool, setSelectedTool] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [newToolId, setNewToolId] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up('sm'));

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [returningTools, setReturningTools] = useState([]);

  useTitle('Tools');

  const { mutateAsync: deleteTool } = useApiDelete('tool');

  const searchFilter = useMemo(() => {
    if (!search) return {};
    return { $or: [{ title: { $regex: search, $options: 'i' } }] };
  }, [search]);

  const {
    data: tools,
    isPending,
    refetch,
  } = useApiQuery('tool', searchFilter, true);
  useLiveData('tool');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowsToDelete, setRowsToDelete] = useState([]);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewToolTitle('');
    setNewToolId(null);
    setLoadingQR(false);
  };

  const handleAddTool = async () => {
    if (!newToolTitle.trim()) {
      toast.warning('Bitte füllen Sie alle Felder aus.', {
        position: 'bottom-right',
        autoClose: 1500,
      });
      return;
    }

    setLoadingQR(true);

    try {
      const response = await api.post('/tool', {
        title: newToolTitle,
        status: 'AVAILABLE',
        takenBy: null,
        takenAt: null,
        toolStatus: 'OK',
        sharedWith: [
          {
            type: 'tenant',
            tenant: me.tenant,
            permissions: {
              read: true,
              update: true,
            },
          },
        ],
      });
      if (response.status === 201 || response.status === 200) {
        toast.success('Werkzeug erfolgreich hinzugefügt!', {
          position: 'bottom-right',
          autoClose: 1500,
        });
        setNewToolId(response.data.insertedId);
        refetch();
      } else {
        toast.error('Unerwartete Antwort vom Server.', {
          position: 'bottom-right',
          autoClose: 1500,
        });
      }
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Werkzeugs.', {
        position: 'bottom-right',
        autoClose: 1500,
      });
    } finally {
      setLoadingQR(false);
    }
  };

  const handleTakeTools = async () => {
    if (!me) {
      toast.error('Kein aktueller Benutzer gefunden.');
      return;
    }
    if (selectedRows.length === 0) return;

    try {
      await Promise.all(
        selectedRows.map((id) =>
          api.put(`/tool/${id}`, {
            status: 'TAKEN',
            takenBy: me._id,
            takenAt: new Date().toISOString(),
          }),
        ),
      );

      toast.success('Werkzeug(e) erfolgreich mitgenommen!', {
        position: 'bottom-right',
        autoClose: 1500,
      });
      refetch();
      setSelectedRows([]);
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error('Fehler beim Mitnehmen.', {
        position: 'bottom-right',
        autoClose: 1500,
      });
    }
  };

  const handleReturnTools = async () => {
    if (selectedRows.length === 0) return;

    try {
      await Promise.all(
        selectedRows.map((id) =>
          api.put(`/tool/${id}`, {
            status: 'AVAILABLE',
            takenBy: null,
            takenAt: null,
            returnedAt: new Date().toISOString(),
          }),
        ),
      );

      toast.success('Werkzeug(e) erfolgreich zurückgelegt!', {
        position: 'bottom-right',
        autoClose: 1500,
      });
      refetch();
      setSelectedRows([]);
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error('Fehler beim Zurücklegen.', {
        position: 'bottom-right',
        autoClose: 1500,
      });
    }
  };

  const handleOpenReturnDialog = () => {
    setReturningTools(selectedRows);
    setReturnDialogOpen(true);
  };

  const handleConfirmReturn = async () => {
    if (!returnComment.trim()) {
      toast.warning('Bitte Kommentar eingeben.', {
        position: 'bottom-right',
        autoClose: 1500,
      });
      return;
    }

    try {
      await Promise.all(
        returningTools.map((id) =>
          api.put(`/tool/${id}`, {
            status: 'AVAILABLE',
            takenBy: null,
            takenAt: null,
            returnedAt: new Date().toISOString(),
            returnComment,
          }),
        ),
      );

      toast.success('Werkzeug(e) erfolgreich zurückgelegt!', {
        position: 'bottom-right',
        autoClose: 1500,
      });
      refetch();
      setSelectedRows([]);
      setReturnDialogOpen(false);
      setReturnComment('');
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error('Fehler beim Zurücklegen.', {
        position: 'bottom-right',
        autoClose: 1500,
      });
    }
  };

  const handleReturnToolsClick = () => {
    if (
      selectedRows.some(
        (id) => tools.find((t) => t._id === id).takenBy !== me._id,
      )
    ) {
      handleOpenReturnDialog();
    } else {
      handleReturnTools();
    }
  };

  useEffect(() => setFilterModel({ items: [] }), [search]);

  const columns = [
    {
      field: 'title',
      headerName: 'Titel',
      flex: 2,
      mobile: true,
      renderCell: ({ row }) => {
        const totalItems = row.items?.reduce(
          (sum, item) => sum + (item.count || 0),
          0,
        );
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" noWrap>
              {row.title} {totalItems > 0 ? `(${totalItems})` : ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'takenBy',
      headerName: 'Mitgenommen von',
      flex: 2,
      renderCell: ({ row }) => {
        if (!row.takenBy || row.status === 'MAINTENANCE') return null;
        const takenUser = users.find((u) => u._id === row.takenBy);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Avatar
              src={
                takenUser?.profilePicture &&
                `${import.meta.env.VITE_FILE_URL}/${takenUser.profilePicture}`
              }
              sx={{ width: 32, height: 32, mr: 1 }}
            />
            <Typography variant="body2" noWrap>
              {takenUser
                ? `${takenUser.firstName} ${takenUser.lastName}`
                : row.takenBy}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'takenAt',
      headerName: 'Mitgenommen am',
      flex: 1,
      renderCell: ({ row }) =>
        row.takenAt &&
        new Date(row.takenAt).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 2,
      mobile: true,
      renderCell: ({ row }) => {
        let label = '';
        let color = 'default';

        if (row.toolStatus === 'BROKE' || row.toolStatus === 'MISSING') {
          label = 'Nicht verfügbar';
          color = 'error';
        } else {
          switch (row.status) {
            case 'AVAILABLE':
              label = 'Verfügbar im Lager';
              color = 'success';
              break;
            case 'TAKEN':
              label = 'Mitgenommen';
              color = 'warning';
              break;
            case 'MAINTENANCE':
              label = 'Nicht verfügbar';
              color = 'error';
              break;
            default:
              return null;
          }
        }

        return (
          <Chip
            label={label}
            color={color}
            variant="outlined"
            sx={{
              minWidth: 140,
              justifyContent: 'center',
              fontWeight: 400,
              mx: sm ? 0 : -1,
            }}
          />
        );
      },
    },
  ];

  const actions = [
    {
      title: 'Hinzufügen',
      label: 'Hinzufügen',
      color: 'primary',
      selection: 'none',
      icon: Add,
      onClick: handleOpenDialog,
    },
    {
      title: 'Löschen',
      label: 'Löschen',
      color: 'error',
      selection: 'multi',
      icon: Delete,
      onClick: () => {
        if (selectedRows.length === 0) return;
        setRowsToDelete(selectedRows);
        setDeleteDialogOpen(true);
      },
    },
    {
      title: 'Mitnehmen',
      label: 'Mitnehmen',
      color: 'primary',
      selection: 'multi',
      icon: TimeToLeave,
      onClick: handleTakeTools,
      disabled:
        selectedRows.length === 0 ||
        selectedRows.some((id) => {
          const tool = tools?.find((t) => t._id === id);
          return !tool || tool.status !== 'AVAILABLE';
        }),
    },
    {
      title: 'Zurücklegen',
      label: 'Zurücklegen',
      color: 'primary',
      selection: 'multi',
      icon: MoveToInbox,
      onClick: handleReturnToolsClick,
      disabled:
        selectedRows.length === 0 ||
        selectedRows.every((id) => {
          const tool = tools?.find((t) => t._id === id);
          return !tool || tool.status !== 'TAKEN';
        }),
    },
  ];

  return (
    <Box sx={{ boxSizing: 'border-box', height: '100%', width: '100%' }}>
      <ListView
        rows={tools}
        columns={columns}
        actions={actions}
        loading={isPending}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        filterModel={filterModel}
        detailComponents={[
          { Component: ToolForm, title: 'Werkzeug' },
          { Component: ToolHistory, title: 'Historie' },
        ]}
        onRowClick={(params) => {
          setSelectedTool(params.row);
          setPanelOpen(true);
        }}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        sx={{ '& .MuiPaper-root': { width: '500px', maxWidth: '95%' } }}
      >
        <DialogTitle>Neues Werkzeug hinzufügen</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100px',
          }}
        >
          {!newToolId && (
            <TextField
              autoFocus
              margin="dense"
              label="Titel"
              fullWidth
              value={newToolTitle}
              onChange={(e) => setNewToolTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          {newToolId && !loadingQR && sm && (
            <QRCodeSVG
              value={`${import.meta.env.VITE_LIVE_URL}tool/${newToolId}`}
              size={256}
            />
          )}

          {newToolId && !loadingQR && !sm && (
            <TextField
              margin="dense"
              label="URL"
              fullWidth
              value={`${import.meta.env.VITE_LIVE_URL}tool/${newToolId}`}
              InputProps={{
                readOnly: true,
              }}
              sx={{ mb: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {newToolId ? (
            <>
              {!sm && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${import.meta.env.VITE_LIVE_URL}tool/${newToolId}`,
                    );
                    toast.success('URL kopiert!', {
                      position: 'bottom-right',
                      autoClose: 1500,
                    });
                  }}
                >
                  Kopieren
                </Button>
              )}

              <Button onClick={handleCloseDialog} variant="contained">
                Schließen
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog}>Abbrechen</Button>

              {/* Hier der neue LoadingButton */}
              <LoadingButton
                onClick={handleAddTool}
                variant="contained"
                loading={loadingQR}
              >
                Hinzufügen
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Löschen bestätigen</DialogTitle>
        <DialogContent>
          <Typography>
            Willst du {rowsToDelete.length} Werkzeug(e) wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              rowsToDelete.forEach((id) => {
                if (selectedItem === id) setSelectedItem(null);
                deleteTool(id);
              });
              setSelectedRows([]);
              setDeleteDialogOpen(false);
            }}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
      >
        <DialogTitle>Werkzeug zurücklegen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Kommentar"
            fullWidth
            value={returnComment}
            onChange={(e) => setReturnComment(e.target.value)}
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleConfirmReturn} variant="contained">
            Zurücklegen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
